import { create } from "zustand";
import {
  addIeIssueType,
  getData,
  getIeIssueTypeList,
  updateIeIssueType,
  COMPLAINT_ENDPOINTS,
} from "../../../../Apis/complaintApi";
import { mapIeIssueBackendErrors } from "../ieIssueFormValidation";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  paginateClientList,
  sortActiveFirst,
  buildClientPagination,
} from "../../utils/complaintListUtils";

const mapIeIssueItem = (item) => ({
  id: item.ieIssueTypeId ?? item.issueTypeId ?? item.ieIssueId ?? item.id,
  ieIssueId: item.ieIssueTypeId ?? item.issueTypeId ?? item.ieIssueId ?? item.id,
  kpiTypeId: item.kpiTypeId,
  kpiTypeName: item.kpiType ?? item.kpiTypeName ?? "",
  issueType: item.issueType ?? "",
  isActive: Boolean(item.isActive),
  status: item.status,
  createdBy: item.createdBy,
  createdDate: item.createdDate,
  modifiedBy: item.modifiedBy,
  modifiedDate: item.modifiedDate,
});

const mapIeIssueList = (response) => {
  if (!response) return [];

  const list =
    response?.data ??
    response?.result ??
    response?.items ??
    (Array.isArray(response) ? response : []);

  return Array.isArray(list) ? list.map(mapIeIssueItem) : [];
};

const mapKpiTypeOption = (item) => ({
  id: item.kpitypeId ?? item.kpiTypeId ?? item.id,
  name: item.kpitype ?? item.kpiType ?? item.name ?? "",
  isActive: true,
});

const getErrorMessage = (error, fallback = "Unable to save Issue Type.") => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData) return responseData;
  return responseData?.message || responseData?.Message || fallback;
};

export const useIeIssueStore = create((set, get) => ({
  ieIssues: [],
  filterOriginalIeIssues: [],
  filteredIeIssues: [],
  kpiTypeOptions: [],
  isLoading: false,
  isSaving: false,
  isKpiLoading: false,
  filters: {
    searchTerm: "",
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  setSearchTerm: (searchTerm) => {
    set({
      filters: { searchTerm },
    });
  },

  resetSearch: async () => {
    set({ filters: { searchTerm: "" } });
    return get().fetchIeIssues({ currentPage: 1 });
  },

  fetchKpiTypeOptions: async () => {
    set({ isKpiLoading: true });

    try {
      const response = await getData(COMPLAINT_ENDPOINTS.GET_KPI_TYPE_DETAILS);
      const list = Array.isArray(response)
        ? response
        : response?.data ?? response?.result ?? [];

      const kpiTypeOptions = list
        .filter((item) => !item.ktype || item.ktype === "IE")
        .map(mapKpiTypeOption);

      set({ kpiTypeOptions, isKpiLoading: false });
      return { success: true, data: kpiTypeOptions };
    } catch (error) {
      console.error("Error fetching KPI Type details:", error);
      set({ kpiTypeOptions: [], isKpiLoading: false });
      return { success: false, error };
    }
  },

  fetchIeIssues: async (options = {}) => {
    const { filters, pagination } = get();
    const currentPage = options.currentPage ?? pagination.currentPage;
    const pageSize = options.pageSize ?? pagination.pageSize;
    const searchTerm = options.searchTerm ?? filters.searchTerm;

    set({ isLoading: true });

    try {
      const response = await getIeIssueTypeList({
        pageIndex: 1,
        pageSize: COMPLAINT_MAX_FETCH_SIZE,
        issueType: searchTerm,
      });

      const sorted = sortActiveFirst(mapIeIssueList(response));
      const pagedRecords = paginateClientList(sorted, currentPage, pageSize);
      const nextPagination = buildClientPagination(
        sorted.length,
        currentPage,
        pageSize
      );

      set({
        ieIssues: sorted,
        filterOriginalIeIssues: sorted,
        filteredIeIssues: pagedRecords,
        pagination: nextPagination,
        isLoading: false,
      });

      return { success: true, data: pagedRecords, pagination: nextPagination };
    } catch (error) {
      console.error("Error fetching IE Issue Types:", error);

      set({
        ieIssues: [],
        filterOriginalIeIssues: [],
        filteredIeIssues: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });

      return { success: false, error };
    }
  },

  fetchAllIeIssuesForExport: async () => {
    const { filters } = get();

    try {
      const response = await getIeIssueTypeList({
        pageIndex: 1,
        pageSize: COMPLAINT_MAX_FETCH_SIZE,
        issueType: filters.searchTerm,
      });

      return sortActiveFirst(mapIeIssueList(response));
    } catch (error) {
      console.error("Error fetching IE Issue Types for export:", error);
      return get().filteredIeIssues;
    }
  },

  addIeIssue: async (values) => {
    set({ isSaving: true });

    try {
      const response = await addIeIssueType({
        issueType: values.issueType,
        kpiTypeId: values.kpiTypeId,
        isActive: values.isActive,
      });

      await get().fetchIeIssues({ currentPage: 1 });
      set({ isSaving: false });

      return {
        success: true,
        data: response,
        message: response?.message,
      };
    } catch (error) {
      const message = getErrorMessage(error);
      set({ isSaving: false });

      return {
        success: false,
        message,
        fieldErrors: mapIeIssueBackendErrors(error?.response?.data, message),
        error,
      };
    }
  },

  updateIeIssue: async (values) => {
    set({ isSaving: true });

    try {
      const response = await updateIeIssueType({
        ieIssueTypeId:
          values.ieIssueId ?? values.issueTypeId ?? values.ieIssueTypeId,
        issueType: values.issueType,
        kpiTypeId: values.kpiTypeId,
        isActive: values.isActive,
      });

      const { pagination } = get();
      await get().fetchIeIssues({ currentPage: pagination.currentPage });
      set({ isSaving: false });

      return {
        success: true,
        data: response,
        message: response?.message,
      };
    } catch (error) {
      const message = getErrorMessage(error);
      set({ isSaving: false });

      return {
        success: false,
        message,
        fieldErrors: mapIeIssueBackendErrors(error?.response?.data, message),
        error,
      };
    }
  },
}));
